import { withStyles, WithStyles } from '@material-ui/core/styles';
import { FC, useEffect, useMemo, useState } from 'react';

import { Tooltip } from '@shared/components/tooltip';

import { styles } from './MultiGroupSelect.styles';
import { GroupSelect, GroupSelectProps, MultiGroupSelectOnChange } from '../GroupSelect';

export interface MultiGroupSelectProps
  extends Omit<GroupSelectProps, 'classes' | 'defaultValue' | 'multiple' | 'tooltip' | 'onChange' | 'value'>,
    WithStyles<typeof styles> {
  defaultValue?: Id[];
  saveOnClose?: boolean;
  value: Id[];
  onChange?: MultiGroupSelectOnChange;
  classes: WithStyles<typeof styles>['classes'] & {
    groupItem?: string;
  };
}

const MultiGroupSelectComponent: FC<MultiGroupSelectProps> = ({
  saveOnClose,
  value,
  onChange,
  ...groupSelectProps
}) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<Id[]>(() => value);

  useEffect(() => {
    setSelectedOptions(value);
  }, [value]);

  const selectedTooltipOptions = useMemo(() => {
    if (!selectedOptions.length) {
      return '';
    }
    return selectedOptions
      .reduce<Id[]>((result, item) => {
        groupSelectProps.groupOptions.forEach((group) => {
          const groupOption = group.options.find((option) => option.id === item);

          if (groupOption) {
            result.push(groupOption.label);
          }
        });

        return result;
      }, [])
      .join(', ');
  }, [groupSelectProps.groupOptions, selectedOptions]);

  return (
    <Tooltip
      classes={{ container: groupSelectProps.classes.tooltip }}
      placement="top"
      title={menuOpen ? '' : selectedTooltipOptions}
    >
      <GroupSelect
        {...groupSelectProps}
        multiple
        value={selectedOptions}
        onChange={(value, option) => {
          setSelectedOptions(value as Id[]);

          if (!saveOnClose && onChange) {
            onChange(value as Id[], option);
          }
        }}
        onClose={() => {
          setMenuOpen(false);

          if (saveOnClose && onChange) {
            onChange(selectedOptions, undefined);
          }
        }}
        onOpen={() => setMenuOpen(true)}
      />
    </Tooltip>
  );
};

export const MultiGroupSelect = withStyles(styles)(MultiGroupSelectComponent);
